/**
 * Fietsvinder: meerstaps-quiz (leeftijd → lengte/binnenbeen → gebruik/trappers
 * → voor wie → budget → resultaat). Antwoorden bepalen wielmaat (canonieke
 * maattabel uit de sectie-blocks) en doelcollectie; het resultaat wordt via de
 * Section Rendering API opgehaald (sections/fietsvinder-resultaten.liquid) met
 * S&D-filterparams. Bij 0 resultaten zakt een fallback-ladder stap voor stap
 * af, altijd met een eerlijke melding. Zonder JS toont de sectie een fallback
 * met links naar de maatgids en collecties; state leeft in sessionStorage.
 */

function initFietsvinder() {
  const root = document.querySelector('[data-fietsvinder]');
  if (!root || root.dataset.bound) return;
  root.dataset.bound = 'true';

  const wizard = root.querySelector('[data-wizard]');
  // alleen volledig ingevulde maat-banden meenemen (een leeggemaakt number-veld
  // in de theme editor rendert null); zonder bruikbare banden: fallback tonen
  const NUMERIEK = ['wielmaat', 'leeftijdMin', 'leeftijdMax', 'lengteMin', 'lengteMax', 'binnenbeenMin', 'binnenbeenMax'];
  const tabel = JSON.parse(root.querySelector('[data-fietsvinder-tabel]').textContent).filter(
    (b) => NUMERIEK.every((k) => typeof b[k] === 'number') && b.lengteMin < b.lengteMax
  );
  if (!tabel.length) {
    root.classList.remove('is-interactief');
    console.warn('Fietsvinder: geen bruikbare maat-banden in de sectie-blocks');
    return;
  }
  const t = JSON.parse(root.querySelector('[data-fietsvinder-teksten]').textContent);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const OPSLAG = 'kfl-fietsvinder';

  const panelen = new Map(
    [...root.querySelectorAll('[data-stap-paneel]')].map((el) => [el.dataset.stapPaneel, el])
  );
  const dots = root.querySelector('[data-dots]');
  const chipsEl = root.querySelector('[data-chips]');
  const terugKnop = root.querySelector('[data-terug]');
  const verderKnop = root.querySelector('[data-verder]');
  const live = root.querySelector('[data-live]');
  const slot = root.querySelector('[data-resultaat-slot]');
  const melding = root.querySelector('[data-resultaat-melding]');
  const kleineNote = root.querySelector('[data-kleine-note]');
  const fout = root.querySelector('[data-resultaat-fout]');
  const foutLink = root.querySelector('[data-fout-link]');
  const verfijnMelding = root.querySelector('[data-verfijn-melding]');
  const fietsje = root.querySelector('[data-fietsje]');
  const carrousel = root.querySelector('[data-carrousel]');
  const lengteRange = root.querySelector('[data-lengte-range]');
  const lengteNummer = root.querySelector('[data-lengte-nummer]');
  const lengteDisplay = root.querySelector('[data-lengte-display]');

  const BEREIK = { lengte: [75, 170], binnenbeen: [30, 90] };

  let state = { stap: 'leeftijd', antwoorden: {} };
  try {
    const bewaard = JSON.parse(sessionStorage.getItem(OPSLAG) || 'null');
    if (bewaard && typeof bewaard.antwoorden === 'object') state = bewaard;
  } catch {
    /* verse start */
  }

  const bewaar = () => {
    try {
      sessionStorage.setItem(OPSLAG, JSON.stringify(state));
    } catch {
      /* opslag is niet kritiek */
    }
  };

  const vul = (tekst, data) => tekst.replace(/\[(\w+)\]/g, (_, k) => String(data[k] ?? ''));

  const formatteerLeeftijd = (maanden) =>
    maanden % 12 === 0 ? String(maanden / 12) : `${Math.floor(maanden / 12)},5`;

  // branch-bewuste lezingen: een antwoord telt alleen als zijn stap in het
  // huidige pad zit (stale antwoorden uit een verlaten leeftijds-branch niet)
  const isJong = () => (state.antwoorden.leeftijd ?? 72) < 48;
  const actieveTrappers = () => (isJong() ? state.antwoorden.trappers : null);
  const actiefGebruik = () => (isJong() ? null : state.antwoorden.gebruik);

  // — stappenpad met branching —
  const pad = () => {
    const a = state.antwoorden;
    const stappen = ['leeftijd', 'lengte'];
    if ((a.leeftijd ?? 72) < 48) {
      stappen.push('trappers');
      if (a.trappers !== 'loopfiets') stappen.push('wie', 'budget');
    } else {
      stappen.push('gebruik');
      if (a.gebruik !== 'bmx') stappen.push('wie');
      stappen.push('budget');
    }
    stappen.push('resultaat');
    return stappen;
  };

  const beantwoord = (stapId) => {
    const a = state.antwoorden;
    switch (stapId) {
      case 'leeftijd':
        return a.leeftijd != null;
      case 'lengte':
        return true; // vooringevuld of bewust overgeslagen
      case 'trappers':
        return a.trappers != null;
      case 'gebruik':
        return a.gebruik != null;
      case 'wie':
        return a.wie != null;
      case 'budget':
        return 'budget' in a;
      default:
        return true;
    }
  };

  // — maatkeuze: binnenbeen > lengte > leeftijd; dichtstbijzijnd bandmidden
  //   wint, gelijkspel = kleinste maat (zelfde regel als de groeimeter) —
  const meetwaarde = () => {
    const a = state.antwoorden;
    if (a.meetwijze === 'binnenbeen' && a.binnenbeen != null) {
      return { min: 'binnenbeenMin', max: 'binnenbeenMax', waarde: a.binnenbeen };
    }
    if (a.lengte != null) return { min: 'lengteMin', max: 'lengteMax', waarde: a.lengte };
    return { min: 'leeftijdMin', max: 'leeftijdMax', waarde: a.leeftijd ?? 72 };
  };

  const kiesMaat = () => {
    const loop = actieveTrappers() === 'loopfiets';
    const banden = tabel.filter((b) => (loop ? b.wielmaat === 0 : b.wielmaat > 0));
    // rij in de theme editor verwijderd: null teruggeven, nooit crashen
    if (!banden.length) return null;
    const { min, max, waarde } = meetwaarde();
    // zelfde regel als de groeimeter: eerst banden waar de waarde ín valt;
    // pas als geen enkele band past, de dichtstbijzijnde (clamp)
    const passend = banden.filter((b) => waarde >= b[min] && waarde <= b[max]);
    const pool = passend.length ? passend : banden;
    return pool
      .map((b, i) => ({ b, i, afstand: Math.abs(waarde - (b[min] + b[max]) / 2) }))
      .sort((x, y) => x.afstand - y.afstand || x.i - y.i)[0].b;
  };

  const onderKleinsteMaat = () => {
    if (actieveTrappers() === 'loopfiets') return false;
    const banden = tabel.filter((b) => b.wielmaat > 0);
    if (!banden.length) return false;
    const { min, waarde } = meetwaarde();
    return waarde < Math.min(...banden.map((b) => b[min]));
  };

  // — fallback-ladder —
  const bouwPogingen = () => {
    const a = state.antwoorden;
    const d = root.dataset;
    const doel = actiefGebruik() === 'bmx' ? d.urlBmx : a.wie === 'meisje' ? d.urlMeisjes : a.wie === 'jongen' ? d.urlJongens : d.urlAlle;
    const prijs = { '0-150': { lte: 150 }, '150-250': { gte: 150, lte: 250 }, '250-': { gte: 250 } }[a.budget] || null;

    // loopfiets-pad: eigen collectie zonder wielmaat-filter (loopfietsen hebben
    // geen wielmaat-metafield); alleen beschikbaarheid + eventueel prijs
    if (actieveTrappers() === 'loopfiets') {
      const maakLoop = (metPrijs, meldingTekst) => {
        const params = new URLSearchParams();
        params.set(d.paramBeschikbaar, '1');
        let verwacht = 1;
        if (metPrijs && prijs) {
          verwacht += 1;
          if (prijs.gte) params.set('filter.v.price.gte', String(prijs.gte));
          if (prijs.lte) params.set('filter.v.price.lte', String(prijs.lte));
        }
        return { url: d.urlLoopfietsen, params, verwacht, melding: meldingTekst };
      };
      const loopPogingen = [maakLoop(true, null)];
      if (prijs) loopPogingen.push(maakLoop(false, vul(t.fallbackPrijs, { maat: kiesMaat()?.label ?? '' })));
      return loopPogingen;
    }

    const maat = kiesMaat();
    if (!maat) {
      // fiets-banden ontbreken in de sectie-blocks (editor-misconfiguratie):
      // eerlijk alle beschikbare fietsen tonen in plaats van blijven hangen
      const params = new URLSearchParams();
      params.set(d.paramBeschikbaar, '1');
      const nood = [{ url: doel, params, verwacht: 1, melding: null }];
      if (doel !== d.urlAlle) nood.push({ url: d.urlAlle, params: new URLSearchParams(params), verwacht: 1, melding: null });
      return nood;
    }
    const maten = tabel.filter((b) => b.wielmaat > 0).map((b) => b);
    const idx = maten.findIndex((b) => b.wielmaat === maat.wielmaat);
    const kleiner = idx > 0 ? maten[idx - 1] : null;
    const groter = idx >= 0 && idx < maten.length - 1 ? maten[idx + 1] : null;

    const maak = (url, band, metPrijs, meldingTekst) => {
      const params = new URLSearchParams();
      params.set(d.paramWielmaat, String(band.wielmaat));
      params.set(d.paramBeschikbaar, '1');
      // prijsfilter telt in Shopify als 1 actieve filter, ook met gte én lte
      let verwacht = 2;
      if (metPrijs && prijs) {
        verwacht += 1;
        if (prijs.gte) params.set('filter.v.price.gte', String(prijs.gte));
        if (prijs.lte) params.set('filter.v.price.lte', String(prijs.lte));
      }
      return { url, params, verwacht, melding: meldingTekst };
    };

    const pogingen = [maak(doel, maat, true, null)];
    if (prijs) pogingen.push(maak(doel, maat, false, vul(t.fallbackPrijs, { maat: maat.label })));
    if (kleiner) pogingen.push(maak(doel, kleiner, false, vul(t.fallbackMaat, { maat: maat.label })));
    if (groter) pogingen.push(maak(doel, groter, false, vul(t.fallbackMaat, { maat: maat.label })));
    if (doel !== d.urlAlle) pogingen.push(maak(d.urlAlle, maat, false, vul(t.fallbackAlle, { maat: maat.label })));
    return pogingen;
  };

  // — resultaat laden —
  let fetchController = null;
  let laatsteBasis = null;
  let verfijning = [];
  const laadResultaat = async () => {
    melding.hidden = true;
    fout.hidden = true;
    kleineNote.hidden = true;
    verfijnMelding.hidden = true;
    laatsteBasis = null;
    verfijning = [];
    laatsteVerfijning = [];
    slot.classList.remove('is-verversen');
    slot.replaceChildren();

    slot.setAttribute('aria-busy', 'true');
    root.classList.add('is-laden');
    live.textContent = t.ariaLaden;

    fetchController?.abort();
    fetchController = new AbortController();
    // bouwPogingen binnen de try: een misconfiguratie (verwijderde blocks)
    // eindigt dan in de nette foutstaat in plaats van een eeuwige spinner
    let terugvalUrl = root.dataset.urlAlle;

    try {
      const pogingen = bouwPogingen();
      const eerste = pogingen[0];
      terugvalUrl = `${eerste.url}?${eerste.params}`;
      for (const poging of pogingen) {
        const fetchParams = new URLSearchParams(poging.params);
        fetchParams.set('section_id', root.dataset.resultatenSectie);
        const res = await fetch(`${poging.url}?${fetchParams}`, { signal: fetchController.signal });
        if (!res.ok) continue;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const vers = doc.querySelector('[data-fietsvinder-resultaten]');
        if (!vers || Number(vers.dataset.resultCount) === 0) continue;
        if (Number(vers.dataset.activeFilters) < poging.verwacht) {
          console.warn('Fietsvinder: niet alle filters zijn actief geworden', String(poging.params));
        }
        for (const el of vers.querySelectorAll('[data-reveal]')) el.classList.add('is-revealed');
        vers.classList.add('is-binnen');
        if (poging.melding) {
          melding.textContent = poging.melding;
          melding.hidden = false;
        }
        if (onderKleinsteMaat()) kleineNote.hidden = false;
        slot.replaceChildren(vers);
        slot.removeAttribute('aria-busy');
        root.classList.remove('is-laden');
        laatsteBasis = { url: poging.url, params: new URLSearchParams(poging.params) };
        live.textContent = t.ariaResultaat;
        return;
      }
      toonFout(terugvalUrl);
    } catch (error) {
      if (error.name === 'AbortError') return;
      toonFout(terugvalUrl);
    }
  };

  const toonFout = (url) => {
    slot.replaceChildren();
    slot.removeAttribute('aria-busy');
    root.classList.remove('is-laden');
    foutLink.href = url;
    fout.hidden = false;
    live.textContent = t.error;
  };

  // — verfijnen op het resultaat (kleur/merk-chips uit de facets) —
  // laatsteVerfijning = de laatst succesvol gerénderde set; bij 0 resultaten of
  // een fout draaien we dáárnaar terug (nooit naar een niet-getoonde tussenstaat)
  let laatsteVerfijning = [];

  const herfetchVerfijning = async (param, waarde) => {
    if (!laatsteBasis) return;
    slot.classList.add('is-verversen');
    verfijnMelding.hidden = true;
    live.textContent = '';
    fetchController?.abort();
    fetchController = new AbortController();
    const fetchParams = new URLSearchParams(laatsteBasis.params);
    for (const [p, w] of verfijning) fetchParams.append(p, w);
    fetchParams.set('section_id', root.dataset.resultatenSectie);
    try {
      const res = await fetch(`${laatsteBasis.url}?${fetchParams}`, { signal: fetchController.signal });
      if (!res.ok) throw new Error(`Verfijnen mislukt: ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const vers = doc.querySelector('[data-fietsvinder-resultaten]');
      if (!vers || Number(vers.dataset.resultCount) === 0) {
        // niets in deze combinatie: terugdraaien en eerlijk melden (ook hoorbaar)
        verfijning = laatsteVerfijning.map((paar) => [...paar]);
        verfijnMelding.textContent = t.refineEmpty;
        verfijnMelding.hidden = false;
        live.textContent = t.refineEmpty;
        slot.classList.remove('is-verversen');
        return;
      }
      for (const el of vers.querySelectorAll('[data-reveal]')) el.classList.add('is-revealed');
      vers.classList.add('is-binnen');
      slot.replaceChildren(vers);
      slot.classList.remove('is-verversen');
      laatsteVerfijning = verfijning.map((paar) => [...paar]);
      live.textContent = vul(t.refineResult, { n: vers.dataset.resultCount });
      // focus terug naar de aangeklikte chip in de nieuwe render (of de kop)
      const doel =
        [...slot.querySelectorAll('[data-verfijn-param]')].find(
          (c) => c.dataset.verfijnParam === param && c.dataset.verfijnWaarde === waarde
        ) ?? panelen.get('resultaat')?.querySelector('[data-vraag]');
      doel?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      verfijning = laatsteVerfijning.map((paar) => [...paar]);
      verfijnMelding.textContent = t.refineError;
      verfijnMelding.hidden = false;
      live.textContent = t.refineError;
      slot.classList.remove('is-verversen');
    }
  };

  slot.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-verfijn-param]');
    if (!chip) return;
    const param = chip.dataset.verfijnParam;
    const waarde = chip.dataset.verfijnWaarde;
    const idx = verfijning.findIndex(([p, w]) => p === param && w === waarde);
    if (idx >= 0) verfijning.splice(idx, 1);
    else verfijning.push([param, waarde]);
    herfetchVerfijning(param, waarde);
  });

  // — chips, dots, navigatie —
  const chipLabel = (stapId) => {
    const a = state.antwoorden;
    if (stapId === 'leeftijd') {
      return a.leeftijd != null ? `${formatteerLeeftijd(a.leeftijd)} ${t.jaar}` : null;
    }
    if (stapId === 'lengte') {
      if (a.meetwijze === 'binnenbeen' && a.binnenbeen != null) return `${a.binnenbeen} ${t.cmBinnenbeen}`;
      if (a.lengte != null) return `${a.lengte} cm`;
      return null;
    }
    const gekozen = wizard.querySelector(`input[name="${stapId}"]:checked`);
    return gekozen?.dataset.chip || null;
  };

  // chip aanklikken = dat antwoord wissen en terug naar die stap
  // (kruisje doet wat het belooft); leeftijd wordt alleen aangepast, niet gewist
  const wisAntwoord = (stapId) => {
    const a = state.antwoorden;
    if (stapId === 'lengte') {
      delete a.lengte;
      delete a.binnenbeen;
      delete a.meetwijze;
      delete a.gemeten;
      const tab = wizard.querySelector('input[name="meetwijze"][value="lengte"]');
      if (tab) tab.checked = true;
    } else if (stapId !== 'leeftijd') {
      delete a[stapId];
      const radio = wizard.querySelector(`input[name="${stapId}"]:checked`);
      if (radio) radio.checked = false;
    }
    bewaar();
  };

  const resetKnop = root.querySelector('.fietsvinder__reset');

  const renderChips = (p) => {
    chipsEl.replaceChildren();
    for (const stapId of p) {
      if (stapId === 'resultaat' || stapId === state.stap) continue;
      if (!beantwoord(stapId)) continue;
      const label = chipLabel(stapId);
      if (!label) continue;
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'fietsvinder__chip';
      chip.dataset.chipStap = stapId;
      chip.setAttribute('aria-label', vul(t.editAnswer, { antwoord: label }));
      // leeftijd wordt aangepast (niet gewist), dus daar geen kruisje beloven
      chip.innerHTML =
        stapId === 'leeftijd'
          ? `<span>${label}</span>`
          : `<span>${label}</span><span aria-hidden="true" class="fietsvinder__chip-x">×</span>`;
      chipsEl.append(chip);
    }
    if (resetKnop) resetKnop.hidden = chipsEl.children.length === 0 && state.stap === 'leeftijd';
  };

  chipsEl.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-chip-stap]');
    if (!chip) return;
    const stapId = chip.dataset.chipStap;
    wisAntwoord(stapId);
    toonStap(stapId, { richting: -1 });
  });

  // het fietsje rijdt naar de actieve stap (op de resultaat-stap: het einde);
  // bij een resize herpositioneren we zonder transitie (geen nasleep-effect)
  const positioneerFietsje = (meteen = false) => {
    if (!fietsje || !dots.children.length) return;
    const index = Math.min(pad().indexOf(state.stap), dots.children.length - 1);
    const doel = dots.children[Math.max(0, index)];
    if (!doel) return;
    if (meteen) fietsje.style.transition = 'none';
    fietsje.style.left = `${dots.offsetLeft + doel.offsetLeft + doel.offsetWidth / 2}px`;
    if (meteen) {
      requestAnimationFrame(() => {
        fietsje.style.transition = '';
      });
    }
  };

  let resizeRaf = null;
  window.addEventListener('resize', () => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      positioneerFietsje(true);
      resizeRaf = null;
    });
  });

  const renderDots = (p, index) => {
    dots.replaceChildren();
    for (let i = 0; i < p.length - 1; i += 1) {
      const dot = document.createElement('li');
      dot.className = 'fietsvinder__dot';
      if (i === index) dot.classList.add('is-actief');
      if (i < index) dot.classList.add('is-klaar');
      dots.append(dot);
    }
    requestAnimationFrame(() => positioneerFietsje());
  };

  const updateVerder = () => {
    verderKnop.disabled = !beantwoord(state.stap);
  };

  let liveTimer = null;
  const toonStap = (id, { focus = true, richting = 1 } = {}) => {
    state.stap = id;
    bewaar();
    const p = pad();
    const index = p.indexOf(id);
    root.dataset.stap = id;
    root.style.setProperty('--stap-richting', String(richting));

    panelen.forEach((el, key) => {
      const actief = key === id;
      el.classList.toggle('is-actief', actief);
      if (actief) el.removeAttribute('inert');
      else el.setAttribute('inert', '');
    });

    terugKnop.hidden = index <= 0;
    verderKnop.hidden = id === 'resultaat';
    updateVerder();
    renderChips(p);
    renderDots(p, index);

    if (id === 'lengte') initLengte();
    if (id === 'resultaat') laadResultaat();

    if (focus) panelen.get(id)?.querySelector('[data-vraag]')?.focus();
    clearTimeout(liveTimer);
    liveTimer = setTimeout(() => {
      if (id !== 'resultaat') {
        const vraag = panelen.get(id)?.querySelector('[data-vraag]')?.textContent.trim() ?? '';
        live.textContent = vul(t.ariaStap, { x: index + 1, y: p.length - 1, vraag });
      }
    }, 350);
  };

  const verder = () => {
    const p = pad();
    const i = p.indexOf(state.stap);
    if (i >= 0 && i < p.length - 1 && beantwoord(state.stap)) toonStap(p[i + 1]);
  };

  const terug = () => {
    const p = pad();
    const i = p.indexOf(state.stap);
    if (i > 0) toonStap(p[i - 1], { richting: -1 });
  };

  // — leeftijd-carrousel (scroll-snap + native radio's) —
  let programmatisch = 0;
  let syncNaarMidden = () => {};
  const centreerKaart = (input, gedrag) => {
    if (!carrousel) return;
    const item = input.closest('li');
    if (!item) return;
    programmatisch += 1;
    const doel = item.offsetLeft - (carrousel.clientWidth - item.clientWidth) / 2;
    carrousel.scrollTo({ left: doel, behavior: gedrag });
    setTimeout(() => {
      programmatisch = Math.max(0, programmatisch - 1);
      // een gebruikersswipe die binnen dit venster viel is genegeerd:
      // eenmalig nasynchroniseren (no-op als de kaart al klopt)
      if (programmatisch === 0) syncNaarMidden();
    }, 450);
  };

  if (carrousel) {
    syncNaarMidden = () => {
      const midden = carrousel.scrollLeft + carrousel.clientWidth / 2;
      let dichtstbij = null;
      let kleinste = Infinity;
      for (const item of carrousel.children) {
        const afstand = Math.abs(item.offsetLeft + item.clientWidth / 2 - midden);
        if (afstand < kleinste) {
          kleinste = afstand;
          dichtstbij = item;
        }
      }
      const input = dichtstbij?.querySelector('input');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    let scrollTimer = null;
    carrousel.addEventListener(
      'scroll',
      () => {
        if (programmatisch > 0) return;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          if (programmatisch > 0) return;
          syncNaarMidden();
        }, 140);
      },
      { passive: true }
    );

    for (const knop of root.querySelectorAll('[data-carrousel-stap]')) {
      knop.addEventListener('click', () => {
        const inputs = [...carrousel.querySelectorAll('input')];
        const huidige = inputs.findIndex((i) => i.checked);
        const volgende = inputs[huidige + Number(knop.dataset.carrouselStap)];
        if (volgende) {
          volgende.checked = true;
          volgende.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  // — lengte-stap —
  const gedrag = () => (reducedMotion.matches ? 'auto' : 'smooth');

  const prefillLengte = () => {
    const a = state.antwoorden;
    const veld = a.meetwijze === 'binnenbeen' ? ['binnenbeenMin', 'binnenbeenMax'] : ['lengteMin', 'lengteMax'];
    const leeftijd = a.leeftijd ?? 72;
    const band = tabel
      .map((b, i) => ({ b, i, afstand: Math.abs(leeftijd - (b.leeftijdMin + b.leeftijdMax) / 2) }))
      .sort((x, y) => x.afstand - y.afstand || x.i - y.i)[0].b;
    return Math.round((band[veld[0]] + band[veld[1]]) / 2);
  };

  const toonLengte = (waarde) => {
    const modus = state.antwoorden.meetwijze === 'binnenbeen' ? 'binnenbeen' : 'lengte';
    const [min, max] = BEREIK[modus];
    const label = modus === 'binnenbeen' ? t.labelBinnenbeen : t.labelLengte;
    const waardetekst = modus === 'binnenbeen' ? `${waarde} ${t.cmBinnenbeen}` : `${waarde} cm`;
    lengteRange.min = String(min);
    lengteRange.max = String(max);
    lengteRange.value = String(waarde);
    lengteRange.setAttribute('aria-label', label);
    lengteRange.setAttribute('aria-valuetext', waardetekst);
    if (document.activeElement !== lengteNummer) lengteNummer.value = String(waarde);
    lengteNummer.min = String(min);
    lengteNummer.max = String(max);
    lengteNummer.setAttribute('aria-label', label);
    lengteDisplay.textContent = `${waarde} cm`;
    root.style.setProperty('--lengte-pct', String(((waarde - min) / (max - min)) * 100));
  };

  const zetLengte = (waarde) => {
    const a = state.antwoorden;
    const modus = a.meetwijze === 'binnenbeen' ? 'binnenbeen' : 'lengte';
    const [min, max] = BEREIK[modus];
    const cm = Math.round(Math.min(max, Math.max(min, waarde)));
    a[modus] = cm;
    a.gemeten = true; // echte meting: overleeft een latere leeftijdswissel
    if (a.meetwijze == null) a.meetwijze = 'lengte';
    bewaar();
    toonLengte(cm);
  };

  const initLengte = () => {
    const a = state.antwoorden;
    const modus = a.meetwijze === 'binnenbeen' ? 'binnenbeen' : 'lengte';
    const huidig = a[modus];
    const keuze = wizard.querySelector(`input[name="meetwijze"][value="${modus}"]`);
    if (keuze) keuze.checked = true;
    toonLengte(huidig ?? prefillLengte());
  };

  // — events —
  wizard.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const a = state.antwoorden;

    if (input.name === 'leeftijd') {
      const nieuw = Number(input.value);
      if (a.leeftijd !== nieuw) {
        const wasJong = a.leeftijd != null ? a.leeftijd < 48 : null;
        a.leeftijd = nieuw;
        // maten hangen aan de leeftijd: voorinvulling verversen zolang er
        // niet zelf gemeten is
        if (!a.gemeten) {
          delete a.lengte;
          delete a.binnenbeen;
        }
        // bij een wissel over de 48-maandengrens vervalt de branch-vraag:
        // state én radio's wissen, anders kaapt een oud antwoord het resultaat
        if (wasJong !== null && wasJong !== nieuw < 48) {
          delete a.trappers;
          delete a.gebruik;
          for (const radio of wizard.querySelectorAll(
            'input[name="trappers"]:checked, input[name="gebruik"]:checked'
          )) {
            radio.checked = false;
          }
        }
      }
      centreerKaart(input, gedrag());
    } else if (input.name === 'meetwijze') {
      a.meetwijze = input.value;
      const modus = input.value === 'binnenbeen' ? 'binnenbeen' : 'lengte';
      toonLengte(a[modus] ?? prefillLengte());
      a[modus] = Number(lengteRange.value);
    } else if (['trappers', 'gebruik', 'wie', 'budget'].includes(input.name)) {
      a[input.name] = input.value;
    }
    bewaar();
    updateVerder();
    renderChips(pad());
    renderDots(pad(), pad().indexOf(state.stap));
  });

  wizard.addEventListener('submit', (event) => {
    event.preventDefault();
    verder();
  });

  lengteRange?.addEventListener('input', () => zetLengte(Number(lengteRange.value)));
  lengteNummer?.addEventListener('input', () => {
    if (lengteNummer.value.trim() === '') return;
    const v = Number(lengteNummer.value);
    const [min, max] = BEREIK[state.antwoorden.meetwijze === 'binnenbeen' ? 'binnenbeen' : 'lengte'];
    if (!Number.isNaN(v) && v >= min && v <= max) zetLengte(v);
  });
  lengteNummer?.addEventListener('change', () => {
    // leeg veld niet als 0 parsen en vastklemmen; gewoon de huidige waarde terugzetten
    if (lengteNummer.value.trim() !== '') {
      const v = Number(lengteNummer.value);
      if (!Number.isNaN(v)) zetLengte(v);
    }
    lengteNummer.value = lengteRange.value;
  });

  root.querySelector('[data-skip-lengte]')?.addEventListener('click', () => {
    const a = state.antwoorden;
    delete a.lengte;
    delete a.binnenbeen;
    delete a.meetwijze;
    delete a.gemeten;
    bewaar();
    verder();
  });

  verderKnop.addEventListener('click', verder);
  terugKnop.addEventListener('click', terug);

  for (const knop of root.querySelectorAll('[data-opnieuw]')) {
    knop.addEventListener('click', () => {
      state = { stap: 'leeftijd', antwoorden: {} };
      try {
        sessionStorage.removeItem(OPSLAG);
      } catch {
        /* niet kritiek */
      }
      wizard.reset();
      const standaard = wizard.querySelector('input[name="leeftijd"][data-standaard]');
      if (standaard) {
        standaard.checked = true;
        // programmatisch aanvinken vuurt geen change-event: state zelf bijwerken,
        // anders blijft de Verder-knop onterecht uitgeschakeld
        state.antwoorden.leeftijd = Number(standaard.value);
      }
      toonStap('leeftijd', { richting: -1 });
      if (standaard) centreerKaart(standaard, 'auto');
    });
  }

  // — blobs: subtiele parallax op scroll —
  if (!reducedMotion.matches) {
    let raf = null;
    window.addEventListener(
      'scroll',
      () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          root.style.setProperty('--scroll-y', String(window.scrollY));
          raf = null;
        });
      },
      { passive: true }
    );
  }

  // — herstel & start —
  const herstel = () => {
    const a = state.antwoorden;
    if (a.leeftijd != null) {
      const input = wizard.querySelector(`input[name="leeftijd"][value="${a.leeftijd}"]`);
      if (input) input.checked = true;
      else delete a.leeftijd;
    }
    if (a.leeftijd == null) {
      const standaard = wizard.querySelector('input[name="leeftijd"][data-standaard]');
      if (standaard) {
        standaard.checked = true;
        a.leeftijd = Number(standaard.value);
      }
    }
    for (const naam of ['trappers', 'gebruik', 'wie', 'budget']) {
      if (a[naam] == null && !(naam === 'budget' && 'budget' in a)) continue;
      const input = wizard.querySelector(`input[name="${naam}"][value="${a[naam]}"]`);
      if (input) input.checked = true;
      else delete a[naam];
    }
    if (!pad().includes(state.stap)) state.stap = 'leeftijd';
  };

  herstel();
  root.classList.add('is-interactief');
  toonStap(state.stap, { focus: false });
  const actieveLeeftijd = wizard.querySelector('input[name="leeftijd"]:checked');
  if (actieveLeeftijd) {
    requestAnimationFrame(() => centreerKaart(actieveLeeftijd, 'auto'));
  }
}

initFietsvinder();

// Theme editor: na een sectie-herrender opnieuw initialiseren (live rendert
// een pagina maar één keer, dus daar is dit nooit nodig)
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', initFietsvinder);
}
